import React from 'react';
import { ShieldCheck, GraduationCap, Users, AlertTriangle, BookOpen, CalendarClock, ClipboardList } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ModuleActivity } from '../../services/moduleActivity';
import { ModuleTile, ModuleStats, ActivityLine, SectionHeading, AblageKarte, AngekuendigtTile } from './ModuleTile';
import { KEINE_AKTIVITAET } from './moduleAccents';
import { KENNZAHLEN_LEER, SifaKennzahlen } from '../../services/sifa/betreuungTypes';
import type { HubModulKennung } from '../../services/appUmfang';

/**
 * Die Modulschluessel des SiFa-Rasters. 'sifa-unterweisung',
 * 'sifa-mitarbeiter', 'sifa-fristen', 'sifa-taetigkeiten' und 'sifa-gbu'
 * werden in App.executeModuleNavigation auf die Ansichten 'module-sifa-*'
 * abgebildet; die uebrigen sind die des SiGeKo-Rasters (Ablage, Begehung).
 */
export type SifaModuleTarget = 'editor' | 'module-archive' | 'module-sent-reports' | 'dokumente' | 'sifa-unterweisung' | 'sifa-mitarbeiter' | 'sifa-fristen' | 'sifa-taetigkeiten' | 'sifa-gbu';

/** Die Kacheln der Betreuung, fuer die Frage „bleibt der Abschnitt leer?". */
const BETREUUNG_KACHELN: readonly HubModulKennung[] = ['sifa-unterweisung', 'sifa-mitarbeiter', 'sifa-fristen', 'sifa-taetigkeiten', 'sifa-gbu'];
/** Nur am Rechner: die Begehungskachel und die angekuendigten Kacheln. */
const BETREUUNG_KACHELN_RECHNER: readonly HubModulKennung[] = [...BETREUUNG_KACHELN, 'module-sigeko-inspection', 'sifa-angekuendigt'];

interface Props {
    onNavigate: (module: SifaModuleTarget) => void;
    onOpenEditor: () => void;
    currentInspectionNumber: number;
    inspectionStats: ModuleStats;
    reportExempt?: boolean;
    isReadOnly?: boolean;
    /** Siehe ProjectModuleGrid: bei Stundenbudget zaehlt die Begehungskachel ohne Nenner. */
    inspectionBudgetCountable?: boolean;
    /** Letzte Aktivitaet je Kachel; 'unterweisung' und 'mitarbeiter' kommen aus letzteAktivitaet (services/sifa). */
    activity?: ModuleActivity;
    /** Bestandene Teilnahmen ueber alle Unterweisungen des Projekts. */
    unterweisungCount?: number;
    /** Zeilen im Mitarbeiterstamm des Projekts (ohne zusammengefuehrte Dubletten). */
    mitarbeiterCount?: number;
    /** Fristen- und Betreuungszeit-Kennzahlen (useSifaHubData.kennzahlen, Nr. 92). */
    kennzahlen?: SifaKennzahlen;
    /** Vereinbarte Einsatzzeit h/Jahr (metadata.sifa.einsatzzeitSollStunden); ohne Soll zeigt die Kachel nur das Ist. */
    sollStunden?: number;
    /**
     * Handy-App (docs/android-app-plan.md 1.4): welche Kacheln gezeigt werden.
     * Fehlt die Angabe, ist alles sichtbar wie bisher (Browser, Tablet-App).
     */
    modulSichtbar?: (kennung: HubModulKennung) => boolean;
}

/** Minuten als Stunden mit einer Nachkommastelle, deutsches Komma: 750 → „12,5". */
const stunden = (minuten: number): string =>
    (Math.round((minuten / 60) * 10) / 10).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** TT.MM.JJJJ aus einem ISO-Datum; fremde Werte bleiben, wie sie sind. */
const datum = (iso: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

/**
 * Das Kachelraster eines SiFa-Dauerprojekts (Projekttyp 'sifa').
 *
 * Ein SiFa-Projekt hat keinen Projektverlauf von 01 bis 06: keine
 * Vorankuendigung, keinen SiGe-Plan, keine Unterlage. Es ist eine
 * unbefristete BETREUUNG, und die Kacheln sind die laufenden Aufgaben darin.
 * Stufe 1 traegt Unterweisungen (per Link durch den Kunden), den
 * Mitarbeiterstamm und die Begehung; Stufe 2 (Nr. 92) die Faelligkeiten
 * und den Taetigkeitsnachweis; dazu die Gefaehrdungsbeurteilungen
 * (docs/sifa-gbu-plan.md). Die angekuendigte Kachel Betriebsanweisungen
 * steht ausgegraut, damit der Kunde sieht, wohin die Betreuung waechst,
 * ohne dass jemand auf eine leere Ansicht klickt.
 *
 * Bausteine aus ModuleTile.tsx — dieselben wie im SiGeKo-Raster, damit ein
 * Nutzer beim Wechsel zwischen den Projekttypen nichts Neues lernen muss.
 */
export const SifaModuleGrid: React.FC<Props> = ({
    onNavigate, onOpenEditor, currentInspectionNumber, inspectionStats, reportExempt = false, isReadOnly = false,
    inspectionBudgetCountable = true, activity = KEINE_AKTIVITAET, unterweisungCount = 0, mitarbeiterCount = 0,
    kennzahlen = KENNZAHLEN_LEER, sollStunden, modulSichtbar,
}) => {
    const isMobile = useIsMobile();
    // Ohne `modulSichtbar` ist jede Kachel sichtbar — Browser und Tablet-App wie bisher.
    const zeige = (kennung: HubModulKennung) => !modulSichtbar || modulSichtbar(kennung);
    // Ein Abschnitt ohne sichtbare Kachel entfaellt samt Ueberschrift.
    const betreuungLeer = (isMobile ? BETREUUNG_KACHELN : BETREUUNG_KACHELN_RECHNER).every((k) => !zeige(k));

    // Ein Zaehler ohne Nenner: `contracted: 1` schaltet nur die Anzeige frei,
    // `countOnly` blendet Balken und Quote aus (siehe ModuleTile.hasQuota).
    const zaehler = (n: number): ModuleStats => (n > 0 ? { used: n, contracted: 1 } : null);

    return (
        <div className="space-y-6 animate-fade-in">

            <AblageKarte onNavigate={onNavigate} activity={activity} isMobile={isMobile} modulSichtbar={modulSichtbar} />

            {/* ============================================================
                BETREUUNG — keine Schrittziffern: nichts hier ist einmalig.
                Unterweisungen und Begehung laufen ueber die ganze Betreuung
                (Badge LAUFEND); der Mitarbeiterstamm ist der Bezugspunkt
                fuer beides und traegt deshalb weder Ziffer noch Badge.
               ============================================================ */}
            {!betreuungLeer && (
            <section>
                <SectionHeading label="Betreuung" hint="Fachkraft für Arbeitssicherheit — laufend, unbefristet" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                    {/* Unterweisungen — auch auf dem Handy: Link kopieren und
                        QR zeigen passiert beim Kunden vor Ort. */}
                    {zeige('sifa-unterweisung') && (
                    <ModuleTile
                        accent="teal"
                        badge="LAUFEND"
                        icon={<GraduationCap className="w-5 h-5 text-teal-600" />}
                        title="Unterweisungen"
                        subtitle={isReadOnly ? 'Nur-Ansicht: Teilnahmen ansehen' : 'Per Link durch den Kunden'}
                        cta="Öffnen"
                        onClick={() => onNavigate('sifa-unterweisung')}
                        stats={zaehler(unterweisungCount)}
                        countOnly
                        countOnlyHint="Bestandene Teilnahmen über alle Unterweisungen dieses Projekts."
                        lastActivity={activity.unterweisung}
                    />
                    )}

                    {zeige('sifa-mitarbeiter') && (
                    <ModuleTile
                        accent="cyan"
                        icon={<Users className="w-5 h-5 text-cyan-600" />}
                        title="Mitarbeiter"
                        subtitle={isReadOnly ? 'Nur-Ansicht: Stammdaten & Historie' : 'Stammdaten, Historie & Fälligkeiten'}
                        cta="Öffnen"
                        onClick={() => onNavigate('sifa-mitarbeiter')}
                        stats={zaehler(mitarbeiterCount)}
                        countOnly
                        countOnlyHint="Personen im Mitarbeiterstamm dieses Projekts."
                        lastActivity={activity.mitarbeiter}
                    />
                    )}

                    {/* Begehung — dieselbe Kachel wie im SiGeKo-Raster, samt
                        reportExempt und Nur-Ansicht (Kommentare dort). Auf dem
                        Handy entfaellt sie: dort steht der Knopf ganz oben im
                        Hub (MobileBegehungButton). */}
                    {!isMobile && zeige('module-sigeko-inspection') && (reportExempt ? (
                        <div title="Begehungen sind für dieses Projekt deaktiviert." className="bg-slate-50 p-5 pb-6 rounded-2xl border border-slate-200 relative overflow-hidden opacity-50 grayscale cursor-not-allowed flex flex-col">
                            <div className="bg-slate-200 w-10 h-10 rounded-xl flex items-center justify-center mb-3 border border-slate-300"><ShieldCheck className="w-5 h-5 text-slate-400" /></div>
                            <span className="text-[10px] font-black tracking-[0.16em] text-slate-300 block mb-0.5">LAUFEND</span>
                            <h3 className="font-bold text-slate-600 text-sm relative z-10">Begehungsbericht</h3>
                            <p className="text-[11px] text-slate-400 mt-0.5 mb-2 relative z-10 flex-1">Für dieses Projekt deaktiviert</p>
                            <ActivityLine value={activity.begehung} label="Letzte Begehung" className="mb-3" />
                            <div className="flex items-center text-slate-400 text-[11px] font-bold relative z-10 cursor-not-allowed">Deaktiviert</div>
                        </div>
                    ) : (
                        <ModuleTile
                            accent="emerald"
                            badge="LAUFEND"
                            icon={<ShieldCheck className="w-5 h-5 text-emerald-600" />}
                            title="Begehungsbericht"
                            subtitle={isReadOnly ? 'Nur-Ansicht: keine neue Begehung' : `Nächster Bericht: Nr. ${currentInspectionNumber}`}
                            cta={isReadOnly ? 'Ansehen' : 'Starten'}
                            onClick={onOpenEditor}
                            stats={inspectionStats}
                            usedOverride={Math.max(0, currentInspectionNumber - 1)}
                            countOnly={!inspectionBudgetCountable}
                            hint={isReadOnly ? 'Nur-Ansicht: Der Bericht lässt sich ansehen, eine neue Begehung wird nicht angelegt.' : undefined}
                            lastActivity={activity.begehung}
                            activityLabel="Letzte Begehung"
                        />
                    ))}

                    {/* Faelligkeiten & Vorsorge — Fristenregister (sifa_fristen).
                        Badge rot mit der Zahl der ueberfaelligen Fristen; der
                        Zaehler oben rechts zeigt die ueberfaelligen, sonst die in
                        den naechsten 60 Tagen faelligen. Auch auf dem Handy: die
                        SiFa schaut vor Ort nach, was ansteht. */}
                    {zeige('sifa-fristen') && (
                    <ModuleTile
                        accent="amber"
                        badge={kennzahlen.fristenUeberfaellig > 0 ? `${kennzahlen.fristenUeberfaellig} ÜBERFÄLLIG` : 'LAUFEND'}
                        badgeTone={kennzahlen.fristenUeberfaellig > 0 ? 'alarm' : 'accent'}
                        icon={<CalendarClock className="w-5 h-5 text-amber-600" />}
                        title="Fälligkeiten & Vorsorge"
                        subtitle={kennzahlen.naechsteFrist
                            ? `Nächste: ${kennzahlen.naechsteFrist.titel} · ${datum(kennzahlen.naechsteFrist.faelligAm)}`
                            : 'Prüffristen, Vorsorge, ASA, Wiederholungen'}
                        cta="Öffnen"
                        onClick={() => onNavigate('sifa-fristen')}
                        stats={zaehler(kennzahlen.fristenUeberfaellig > 0 ? kennzahlen.fristenUeberfaellig : kennzahlen.fristenBald)}
                        countOnly
                        countOnlyHint={kennzahlen.fristenUeberfaellig > 0 ? 'Überfällige Fristen (fällig vor heute, nicht erledigt).' : 'Fristen, die in den nächsten 60 Tagen fällig werden.'}
                        lastActivity={activity.fristen}
                    />
                    )}

                    {/* Taetigkeitsnachweis — Betreuungszeiten (sifa_taetigkeiten)
                        gegen die Soll-Einsatzzeit aus den Betriebsdaten. */}
                    {zeige('sifa-taetigkeiten') && (
                    <ModuleTile
                        accent="indigo"
                        badge="LAUFEND"
                        icon={<ClipboardList className="w-5 h-5 text-indigo-600" />}
                        title="Tätigkeitsnachweis"
                        subtitle={sollStunden
                            ? `${stunden(kennzahlen.minutenJahr)} h erbracht / ${sollStunden} h Soll dieses Jahr`
                            : `${stunden(kennzahlen.minutenJahr)} h erbracht dieses Jahr`}
                        cta="Öffnen"
                        onClick={() => onNavigate('sifa-taetigkeiten')}
                        stats={zaehler(kennzahlen.taetigkeitenJahr)}
                        countOnly
                        countOnlyHint="Erfasste Tätigkeiten im laufenden Kalenderjahr."
                        lastActivity={activity.taetigkeiten}
                    />
                    )}

                    {/* Gefaehrdungsbeurteilungen (docs/sifa-gbu-plan.md) — die
                        QUELLE der Betreuung: was sie festlegt, erscheint spaeter
                        in Betriebsanweisungen, Vorsorge und Gefahrstoffverzeichnis.
                        Ersetzt die angekuendigte Kachel; Badge LAUFEND, weil
                        jede GBU regelmaessig ueberprueft und fortgeschrieben
                        wird. In der Handy-App aus (HUB_IN_APP 'sifa-gbu'), im
                        mobilen Browser sichtbar wie die Faelligkeiten. */}
                    {zeige('sifa-gbu') && (
                    <ModuleTile
                        accent="violet"
                        badge="LAUFEND"
                        icon={<AlertTriangle className="w-5 h-5 text-violet-600" />}
                        title="Gefährdungsbeurteilungen"
                        subtitle={isReadOnly ? 'Nur-Ansicht: Beurteilungen ansehen' : 'Je Tätigkeit und Arbeitsbereich nach § 5 ArbSchG'}
                        cta="Öffnen"
                        onClick={() => onNavigate('sifa-gbu')}
                        lastActivity={activity.gbu}
                    />
                    )}

                    {/* Angekuendigt — Stufe 4 des GBU-Plans. Auf dem Handy
                        weggelassen: dort zaehlt jede Kachel, die nichts tut,
                        doppelt. */}
                    {!isMobile && zeige('sifa-angekuendigt') && (
                        <AngekuendigtTile
                            icon={<BookOpen className="w-5 h-5 text-slate-400" />}
                            title="Betriebsanweisungen"
                            subtitle="Stoffe, Maschinen, Tätigkeiten — aus der Gefährdungsbeurteilung"
                        />
                    )}
                </div>
            </section>
            )}
        </div>
    );
};
